import React, { Fragment, useEffect, useState } from "react";
import { Rating } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import _ from "lodash";
import "react-image-gallery/styles/css/image-gallery.css";
import ReactImageGallery from "react-image-gallery";
import { MoonLoader } from "react-spinners";
import DeleteIcon from "../../icons/DeleteIcon";
import BuyNowIcon from "../../icons/BuyNowIcon";
import { checkLoggedIn } from "../../utils";
import { useLoginModal } from "../../Context/LoginModalContext";

// Helpers — same parsing as the product page so the new backend fields render the same way
const parseLines = (str) =>
  typeof str === "string"
    ? str.split(/\r?\n/).map((s) => s.trim()).filter(Boolean)
    : [];

const parsePackageDetails = (str) => {
  const lines = parseLines(str);
  const out = [];
  for (let i = 0; i < lines.length; i += 2) {
    const label = lines[i];
    const value = lines[i + 1];
    if (label && value) out.push({ label, value });
  }
  return out;
};

const hasContent = (v) => {
  if (v == null) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return true;
};

const CURRENCY_SYMBOLS = { EUR: "€", USD: "$", AED: "AED" };

const ViewModal = ({
  open,
  onClose,
  addToCartProduct,
  product,
  carts,
  removeFromCart,
  decrementQuantity,
  ...props
}) => {
  const navigate = useNavigate();
  const { openLoginModal } = useLoginModal();
  const [loading, setLoading] = useState(false);

  const inCart = carts?.find((cart) => cart.id === product?.id);

  // Close on ESC + lock body scroll
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, loading]);

  if (!open) return null;

  const handleAddToCart = async () => {
    if (!checkLoggedIn()) {
      onClose?.();
      openLoginModal();
      return;
    }
    setLoading(true);
    await addToCartProduct(product);
    setLoading(false);
  };

  const handleIncrementQuantity = async () => {
    if (!checkLoggedIn()) {
      onClose?.();
      openLoginModal();
      return;
    }
    setLoading(true);
    await props?.incrementQuantity(product.id);
    setLoading(false);
  };

  const handleDecrementQuantity = async () => {
    if (!checkLoggedIn()) {
      onClose?.();
      openLoginModal();
      return;
    }
    setLoading(true);
    if (inCart?.qty === 1) {
      await removeFromCart(product.id);
    } else {
      await decrementQuantity(product.id);
    }
    setLoading(false);
  };

  const handleBuyNow = async () => {
    if (!checkLoggedIn()) {
      onClose?.();
      openLoginModal();
      return;
    }
    if (inCart) {
      onClose?.();
      navigate("/cart");
      return;
    }
    await handleAddToCart();
    onClose?.();
    navigate("/cart");
  };

  const currencyCode =
    typeof window !== "undefined" ? localStorage.getItem("currency") : "AED";
  const currencyPrefix = CURRENCY_SYMBOLS[currencyCode]
    ? CURRENCY_SYMBOLS[currencyCode] + " "
    : "AED ";


  const descParagraphs = parseLines(product?.description);
  const includedItems = parseLines(product?.whatsIncluded);
  const notIncludedItems = parseLines(product?.whatsNotIncluded);
  const packageRows = parsePackageDetails(product?.packageDetails);
  const legalNote = product?.legalNote;
  const ratingNum = Number(product?.five_start_rating) || 0;

  const hasDetailsBlock =
    includedItems.length > 0 ||
    notIncludedItems.length > 0 ||
    packageRows.length > 0 ||
    hasContent(legalNote);

  return (
    <Fragment>
      <div
        className="gpqv-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gpqv-title"
        onClick={() => !loading && onClose?.()}
      >
        <div className="gpqv-modal" onClick={(e) => e.stopPropagation()}>
          {/* HEADER */}
          <div className="gpqv-head">
            <div className="gpqv-head-eyebrow">
              <span className="gpqv-head-dot" />
              QUICK VIEW
            </div>
            <button
              type="button"
              className="gpqv-close"
              onClick={onClose}
              disabled={loading}
              aria-label="Close"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* SCROLLABLE BODY */}
          <div className="gpqv-body">
            <div className="gpqv-grid">
              {/* IMAGE */}
              <div className="gpqv-image-col">
                <div className="gpqv-image-wrap">
                  {product?.badges?.length > 0 && (
                    <ul className="gpqv-badges">
                      {product.badges.map((badge, i) => (
                        <li key={badge + i} className="gpqv-badge">{badge}</li>
                      ))}
                    </ul>
                  )}
                  {!_?.isEmpty(product?.proimg) ? (
                    <ReactImageGallery
                      showPlayButton={false}
                      autoPlay={false}
                      showFullscreenButton={false}
                      showNav
                      items={product?.proimg?.map((image) => ({
                        original: image?.src,
                        thumbnail: image?.src,
                      }))}
                    />
                  ) : (
                    <div className="gpqv-image-placeholder">★</div>
                  )}
                </div>
              </div>

              {/* INFO */}
              <div className="gpqv-info-col">
                {/* Title */}
                <h2 id="gpqv-title" className="gpqv-title">
                  {product?.title || "Product"}
                </h2>

                {/* Rating */}
                {ratingNum > 0 && (
                  <div className="gpqv-rating">
                    <Rating
                      name="read-only"
                      precision={0.5}
                      value={ratingNum}
                      readOnly
                      size="small"
                      sx={{
                        "& .MuiRating-iconFilled": { color: "#d4a857" },
                        "& .MuiRating-iconEmpty": { color: "#3a2f1c" },
                      }}
                    />
                    <span className="gpqv-rating-num">{ratingNum}</span>
                    <span className="gpqv-rating-divider">·</span>
                    <span className="gpqv-rating-label">verified rating</span>
                  </div>
                )}

                {/* Price */}
                <div className="gpqv-price-row">
                  <div>
                    <span className="gpqv-price-label">Price</span>
                    <div className="gpqv-price">
                      {currencyPrefix}{product?.price}
                    </div>
                  </div>
                </div>

                {/* Description */}
                {descParagraphs.length > 0 && (
                  <div className="gpqv-desc">
                    {descParagraphs.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                )}

                {/* Quick package facts (max first 4 rows in the side col) */}
                {packageRows.length > 0 && (
                  <div className="gpqv-quick-facts">
                    {packageRows.slice(0, 4).map((row, i) => (
                      <div className="gpqv-fact" key={i}>
                        <span className="gpqv-fact-label">{row.label}</span>
                        <span className="gpqv-fact-val">{row.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* DETAILS BLOCK — only renders if any of these fields exist */}
            {hasDetailsBlock && (
              <div className="gpqv-details">
                <div className="gpqv-details-grid">
                  {includedItems.length > 0 && (
                    <article className="gpqv-detail-card">
                      <div className="gpqv-detail-head">
                        <span className="gpqv-detail-icon gpqv-detail-icon-good">
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                          </svg>
                        </span>
                        <h3>What's Included</h3>
                      </div>
                      <ul className="gpqv-detail-list">
                        {includedItems.map((item, i) => (
                          <li key={i} className="gpqv-li-good">
                            <span className="gpqv-li-mark">
                              <svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                              </svg>
                            </span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </article>
                  )}

                  {notIncludedItems.length > 0 && (
                    <article className="gpqv-detail-card">
                      <div className="gpqv-detail-head">
                        <span className="gpqv-detail-icon gpqv-detail-icon-bad">
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </span>
                        <h3>What's Not Included</h3>
                      </div>
                      <ul className="gpqv-detail-list">
                        {notIncludedItems.map((item, i) => (
                          <li key={i} className="gpqv-li-bad">
                            <span className="gpqv-li-mark">
                              <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </article>
                  )}

                  {packageRows.length > 4 && (
                    <article className="gpqv-detail-card gpqv-detail-card-wide">
                      <div className="gpqv-detail-head">
                        <span className="gpqv-detail-icon">
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                            <path d="M20 6h-2.18c.11-.31.18-.65.18-1a3 3 0 00-5.5-1.65l-.5.67-.5-.68A2.99 2.99 0 009 2 3 3 0 006 5c0 .35.07.69.18 1H4a2 2 0 00-1.99 2L2 19a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2zM9 4a1 1 0 110 2 1 1 0 010-2zm6 0a1 1 0 110 2 1 1 0 010-2z" />
                          </svg>
                        </span>
                        <h3>Full Package Details</h3>
                      </div>
                      <dl className="gpqv-pkg-grid">
                        {packageRows.slice(4).map((row, i) => (
                          <div className="gpqv-pkg-row" key={i}>
                            <dt>{row.label}</dt>
                            <dd>{row.value}</dd>
                          </div>
                        ))}
                      </dl>
                    </article>
                  )}

                  {hasContent(legalNote) && (
                    <article className="gpqv-detail-card gpqv-detail-card-wide gpqv-legal">
                      <div className="gpqv-legal-icon">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                          <path d="M12 2L1 21h22L12 2zm-1 8h2v6h-2v-6zm0 8h2v2h-2v-2z" />
                        </svg>
                      </div>
                      <div>
                        <strong>Important note</strong>
                        <p>{legalNote}</p>
                      </div>
                    </article>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* FOOTER ACTIONS */}
          <div className="gpqv-foot">
            {product?.slug && (
              <Link
                to={`/product-single/${product.slug}`}
                className="gpqv-view-full"
                onClick={onClose}
              >
                View full details
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </Link>
            )}

            <div className="gpqv-foot-actions">
              {inCart ? (
                <div className="gpqv-qty">
                  <button
                    type="button"
                    onClick={handleDecrementQuantity}
                    disabled={loading}
                    aria-label="Decrease"
                  >
                    {loading ? (
                      <MoonLoader size={12} color="#d4a857" />
                    ) : inCart.qty === 1 ? (
                      <DeleteIcon />
                    ) : (
                      "−"
                    )}
                  </button>
                  <span className="gpqv-qty-val">{inCart?.qty}</span>
                  <button
                    type="button"
                    onClick={handleIncrementQuantity}
                    disabled={loading}
                    aria-label="Increase"
                  >
                    {loading ? <MoonLoader size={12} color="#d4a857" /> : "+"}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="gpqv-cart-btn"
                  onClick={handleAddToCart}
                  disabled={loading}
                >
                  {loading ? (
                    <MoonLoader size={14} color="#d4a857" />
                  ) : (
                    <>
                      <i className="icon-cart" />
                      <span>Add to cart</span>
                    </>
                  )}
                </button>
              )}

              <button
                type="button"
                className="gpqv-buy-btn"
                onClick={handleBuyNow}
                disabled={loading}
              >
                {loading ? (
                  <MoonLoader size={14} color="#0b0b0c" />
                ) : (
                  <>
                    <BuyNowIcon />
                    <span>Buy Now</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .gpqv-backdrop {
          position: fixed; inset: 0;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          z-index: 1600;
          animation: gpqv-fade .2s ease;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }
        @keyframes gpqv-fade { from { opacity: 0; } to { opacity: 1; } }

        .gpqv-modal {
          position: relative;
          width: 100%;
          max-width: 1100px;
          max-height: 92vh;
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px solid rgba(212, 168, 87, 0.3);
          border-radius: 20px;
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6);
          display: flex;
          flex-direction: column;
          color: #e6e8ec;
          animation: gpqv-pop .28s cubic-bezier(0.34, 1.56, 0.64, 1);
          overflow: hidden;
        }
        @keyframes gpqv-pop {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }

        /* HEADER */
        .gpqv-head {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 22px;
          border-bottom: 1px solid rgba(212, 168, 87, 0.18);
          flex-shrink: 0;
        }
        .gpqv-head-eyebrow {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 5px 12px;
          background: rgba(212, 168, 87, 0.1);
          border: 1px solid rgba(212, 168, 87, 0.3);
          color: #d4a857;
          border-radius: 999px;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 1.8px;
        }
        .gpqv-head-dot {
          width: 5px; height: 5px;
          border-radius: 50%;
          background: #d4a857;
          box-shadow: 0 0 8px #d4a857;
          animation: gpqv-pulse 1.6s ease-in-out infinite;
        }
        @keyframes gpqv-pulse { 0%,100% { opacity:1; transform: scale(1); } 50% { opacity:.55; transform: scale(.85); } }
        .gpqv-close {
          width: 34px; height: 34px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          color: #9aa0a8;
          border-radius: 9px;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          font-family: inherit;
          transition: background .2s ease, color .2s ease, border-color .2s ease;
        }
        .gpqv-close:hover:not(:disabled) {
          background: rgba(212,168,87,0.15);
          border-color: rgba(212,168,87,0.4);
          color: #d4a857;
        }
        .gpqv-close:disabled { opacity: 0.5; cursor: not-allowed; }

        /* BODY */
        .gpqv-body {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
        }
        .gpqv-body::-webkit-scrollbar { width: 6px; }
        .gpqv-body::-webkit-scrollbar-thumb { background: rgba(212,168,87,0.25); border-radius: 999px; }

        .gpqv-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: 24px;
          align-items: flex-start;
        }

        /* IMAGE */
        .gpqv-image-wrap {
          position: relative;
          background: #0b0b0c;
          border: 1px solid rgba(212, 168, 87, 0.22);
          border-radius: 14px;
          padding: 14px;
          overflow: hidden;
        }
        .gpqv-badges {
          position: absolute;
          top: 14px; left: 14px;
          list-style: none; padding: 0; margin: 0;
          display: flex; flex-wrap: wrap; gap: 6px;
          z-index: 3;
        }
        .gpqv-badge {
          background: linear-gradient(135deg, #d4a857, #b58a3f);
          color: #1a1208;
          font-weight: 700;
          font-size: 10px;
          letter-spacing: 0.5px;
          padding: 4px 10px;
          border-radius: 999px;
          box-shadow: 0 4px 10px rgba(212, 168, 87, 0.3);
          text-transform: uppercase;
        }
        .gpqv-image-placeholder {
          aspect-ratio: 1 / 1;
          display: flex; align-items: center; justify-content: center;
          color: rgba(212, 168, 87, 0.3);
          font-size: 80px;
        }
        .gpqv-image-wrap .image-gallery,
        .gpqv-image-wrap .image-gallery-content { background: transparent !important; }
        .gpqv-image-wrap .image-gallery-image {
          max-height: 380px !important;
          object-fit: contain !important;
        }
        .gpqv-image-wrap .image-gallery-slide {
          background: #0b0b0c;
          border-radius: 10px;
          overflow: hidden;
        }
        .gpqv-image-wrap .image-gallery-thumbnails-wrapper { margin-top: 12px !important; }
        .gpqv-image-wrap .image-gallery-thumbnail {
          border: 2px solid rgba(212, 168, 87, 0.18) !important;
          border-radius: 8px !important;
          overflow: hidden;
          background: #0b0b0c;
          transition: border-color .25s ease, transform .25s ease !important;
        }
        .gpqv-image-wrap .image-gallery-thumbnail:hover {
          border-color: rgba(212, 168, 87, 0.5) !important;
          transform: translateY(-2px);
        }
        .gpqv-image-wrap .image-gallery-thumbnail.active,
        .gpqv-image-wrap .image-gallery-thumbnail:focus {
          border-color: #d4a857 !important;
          box-shadow: 0 4px 12px rgba(212, 168, 87, 0.3);
        }
        .gpqv-image-wrap .image-gallery-thumbnail-image {
          object-fit: cover !important;
          max-height: 60px !important;
        }
        .gpqv-image-wrap .image-gallery-icon {
          color: #d4a857 !important;
          filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.6));
        }
        .gpqv-image-wrap .image-gallery-icon:hover { color: #f1cf85 !important; }
        .gpqv-image-wrap .image-gallery-left-nav,
        .gpqv-image-wrap .image-gallery-right-nav { padding: 0 12px !important; }

        /* INFO */
        .gpqv-info-col { display: flex; flex-direction: column; gap: 14px; min-width: 0; }
        .gpqv-meta { display: flex; flex-wrap: wrap; gap: 8px; }
        .gpqv-meta-chip {
          display: inline-flex; align-items: center;
          padding: 5px 12px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(212,168,87,0.2);
          color: #cfd2d6;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.4px;
        }
        .gpqv-meta-chip-brand {
          color: #d4a857;
          background: rgba(212,168,87,0.1);
          border-color: rgba(212,168,87,0.35);
          font-weight: 700;
          letter-spacing: 0.6px;
          text-transform: uppercase;
        }
        .gpqv-title {
          font-family: "Playfair Display", "Times New Roman", serif;
          font-size: 26px;
          font-weight: 700;
          color: #fff;
          margin: 0;
          line-height: 1.2;
          letter-spacing: -0.2px;
        }
        .gpqv-rating {
          display: flex; align-items: center; gap: 6px;
          font-size: 12.5px;
          color: #9aa0a8;
        }
        .gpqv-rating-num { color: #d4a857; font-weight: 700; }
        .gpqv-rating-divider { color: #4b5563; }

        .gpqv-price-row {
          display: flex; align-items: flex-end; gap: 18px;
          padding: 14px 0;
          border-top: 1px solid rgba(212,168,87,0.12);
          border-bottom: 1px solid rgba(212,168,87,0.12);
          flex-wrap: wrap;
        }
        .gpqv-price-label, .gpqv-price-old-label {
          display: block;
          font-size: 10px;
          color: #6b7280;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          margin-bottom: 4px;
          font-weight: 700;
        }
        .gpqv-price {
          font-size: 28px;
          font-weight: 800;
          background: linear-gradient(135deg, #f1cf85, #b58a3f);
          -webkit-background-clip: text; background-clip: text; color: transparent;
          line-height: 1;
        }
        .gpqv-price-old-wrap { display: inline-flex; flex-direction: column; }
        .gpqv-price-old {
          font-size: 15px;
          color: #6b7280;
          text-decoration: line-through;
          font-weight: 600;
        }

        /* STOCK */
        .gpqv-stock { display: flex; flex-direction: column; gap: 8px; }
        .gpqv-stock-row {
          display: flex; align-items: center; gap: 8px;
          font-size: 13px; font-weight: 500;
        }
        .gpqv-stock-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .gpqv-stock-high .gpqv-stock-dot { background: #00d488; box-shadow: 0 0 8px rgba(0,212,136,0.6); }
        .gpqv-stock-high .gpqv-stock-label { color: #00d488; }
        .gpqv-stock-med .gpqv-stock-dot { background: #f1cf85; box-shadow: 0 0 8px rgba(241,207,133,0.6); }
        .gpqv-stock-med .gpqv-stock-label { color: #f1cf85; }
        .gpqv-stock-low .gpqv-stock-dot {
          background: #ff8c5a; box-shadow: 0 0 8px rgba(255,140,90,0.6);
          animation: gpqv-blink 1.4s ease-in-out infinite;
        }
        .gpqv-stock-low .gpqv-stock-label { color: #ff8c5a; }
        .gpqv-stock-out .gpqv-stock-dot { background: #ff5a5f; }
        .gpqv-stock-out .gpqv-stock-label { color: #ff5a5f; }
        @keyframes gpqv-blink { 0%,100% { opacity:1; } 50% { opacity:.45; } }
        .gpqv-stock-bar {
          height: 4px;
          background: rgba(255,255,255,0.05);
          border-radius: 999px;
          overflow: hidden;
        }
        .gpqv-stock-bar-fill {
          display: block; height: 100%;
          background: linear-gradient(90deg, #d4a857, #f1cf85);
          border-radius: 999px;
        }
        .gpqv-stock-low .gpqv-stock-bar-fill { background: linear-gradient(90deg, #ff8c5a, #f1cf85); }

        /* DESCRIPTION */
        .gpqv-desc {
          display: flex; flex-direction: column; gap: 8px;
          color: #c5c8cf;
          font-size: 13.5px;
          line-height: 1.65;
        }
        .gpqv-desc p { margin: 0; }

        /* QUICK FACTS (in side col) */
        .gpqv-quick-facts {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }
        .gpqv-fact {
          display: flex; flex-direction: column; gap: 3px;
          padding: 10px 12px;
          background: rgba(0,0,0,0.25);
          border: 1px solid rgba(212,168,87,0.18);
          border-radius: 10px;
        }
        .gpqv-fact-label {
          font-size: 10px;
          color: #d4a857;
          font-weight: 700;
          letter-spacing: 1.4px;
          text-transform: uppercase;
        }
        .gpqv-fact-val {
          font-size: 12.5px;
          color: #fff;
          font-weight: 600;
          line-height: 1.4;
        }

        /* DETAILS BLOCK */
        .gpqv-details {
          margin-top: 24px;
          padding-top: 22px;
          border-top: 1px dashed rgba(212,168,87,0.22);
        }
        .gpqv-details-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }
        .gpqv-detail-card {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(212,168,87,0.18);
          border-radius: 12px;
          padding: 18px;
        }
        .gpqv-detail-card-wide { grid-column: 1 / -1; }
        .gpqv-detail-head {
          display: flex; align-items: center; gap: 10px;
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 1px solid rgba(212,168,87,0.14);
        }
        .gpqv-detail-head h3 {
          font-family: "Playfair Display", serif;
          font-size: 15px; color: #fff; margin: 0; font-weight: 700;
        }
        .gpqv-detail-icon {
          width: 32px; height: 32px;
          background: rgba(212,168,87,0.12);
          border: 1px solid rgba(212,168,87,0.3);
          color: #d4a857;
          border-radius: 9px;
          display: inline-flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .gpqv-detail-icon-good {
          background: rgba(74,222,128,0.12);
          border-color: rgba(74,222,128,0.32);
          color: #4ade80;
        }
        .gpqv-detail-icon-bad {
          background: rgba(255,90,90,0.1);
          border-color: rgba(255,90,90,0.3);
          color: #ff7a7a;
        }
        .gpqv-detail-list {
          list-style: none; padding: 0; margin: 0;
          display: flex; flex-direction: column; gap: 6px;
        }
        .gpqv-detail-list li {
          display: flex; align-items: flex-start; gap: 9px;
          font-size: 13px; color: #c5c8cf; line-height: 1.5;
        }
        .gpqv-li-mark {
          width: 16px; height: 16px;
          border-radius: 50%;
          display: inline-flex; align-items: center; justify-content: center;
          flex-shrink: 0; margin-top: 1px;
        }
        .gpqv-li-good .gpqv-li-mark {
          background: rgba(74,222,128,0.15);
          border: 1px solid rgba(74,222,128,0.32);
          color: #4ade80;
        }
        .gpqv-li-bad .gpqv-li-mark {
          background: rgba(255,90,90,0.12);
          border: 1px solid rgba(255,90,90,0.3);
          color: #ff7a7a;
        }

        .gpqv-pkg-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px; margin: 0;
        }
        .gpqv-pkg-row {
          display: flex; flex-direction: column; gap: 3px;
          padding: 10px 12px;
          background: rgba(0,0,0,0.25);
          border: 1px solid rgba(212,168,87,0.14);
          border-radius: 9px;
        }
        .gpqv-pkg-row dt {
          font-size: 10px;
          color: #d4a857;
          font-weight: 700;
          letter-spacing: 1.3px;
          text-transform: uppercase;
          margin: 0;
        }
        .gpqv-pkg-row dd {
          font-size: 12.5px;
          color: #fff;
          font-weight: 600;
          margin: 0;
          line-height: 1.4;
        }

        .gpqv-legal {
          display: grid;
          grid-template-columns: 38px 1fr;
          gap: 12px;
          align-items: flex-start;
          background: rgba(212,168,87,0.06);
          border: 1px solid rgba(212,168,87,0.28);
          border-left: 3px solid #d4a857;
        }
        .gpqv-legal-icon {
          width: 38px; height: 38px;
          background: rgba(212,168,87,0.14);
          border: 1px solid rgba(212,168,87,0.35);
          color: #d4a857;
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
        }
        .gpqv-legal strong {
          display: block;
          font-family: "Playfair Display", serif;
          font-size: 13px;
          color: #d4a857;
          font-weight: 700;
          margin-bottom: 3px;
        }
        .gpqv-legal p {
          font-size: 13px;
          color: #c5c8cf;
          line-height: 1.55;
          margin: 0;
        }

        /* FOOTER */
        .gpqv-foot {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          padding: 16px 22px;
          border-top: 1px solid rgba(212,168,87,0.18);
          background: rgba(0,0,0,0.3);
          flex-shrink: 0;
        }
        .gpqv-view-full {
          display: inline-flex; align-items: center; gap: 6px;
          color: #d4a857;
          font-size: 12.5px;
          font-weight: 700;
          letter-spacing: 0.4px;
          text-transform: uppercase;
          text-decoration: none;
          transition: color .2s ease, gap .2s ease;
        }
        .gpqv-view-full:hover {
          color: #f1cf85;
          gap: 10px;
          text-decoration: none;
        }

        .gpqv-foot-actions {
          display: inline-flex; align-items: center; gap: 10px;
          flex-wrap: wrap;
        }

        .gpqv-cart-btn {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(212,168,87,0.4);
          color: #d4a857;
          padding: 12px 18px;
          border-radius: 10px;
          font-size: 12.5px;
          font-weight: 700;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          cursor: pointer;
          font-family: inherit;
          min-height: 44px;
          transition: background .2s ease, border-color .2s ease;
        }
        .gpqv-cart-btn:hover:not(:disabled) {
          background: rgba(212,168,87,0.12);
          border-color: #d4a857;
        }
        .gpqv-cart-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .gpqv-buy-btn {
          display: inline-flex; align-items: center; gap: 9px;
          background: linear-gradient(135deg, #f1cf85, #d4a857 50%, #b58a3f);
          color: #0b0b0c;
          border: 1px solid rgba(255,232,178,0.55);
          border-radius: 10px;
          padding: 12px 22px;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          cursor: pointer;
          font-family: inherit;
          min-height: 44px;
          box-shadow: 0 6px 18px rgba(212,168,87,0.3);
          transition: transform .2s ease, box-shadow .25s ease, filter .2s ease;
        }
        .gpqv-buy-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 24px rgba(212,168,87,0.4);
          filter: brightness(1.05);
        }
        .gpqv-buy-btn:disabled { opacity: 0.65; cursor: not-allowed; }

        .gpqv-qty {
          display: inline-flex; align-items: center;
          background: rgba(0,0,0,0.4);
          border: 1px solid rgba(212,168,87,0.4);
          border-radius: 10px;
          overflow: hidden;
        }
        .gpqv-qty button {
          background: transparent;
          border: none;
          color: #d4a857;
          width: 38px; height: 44px;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          font-family: inherit;
        }
        .gpqv-qty button:hover:not(:disabled) { background: rgba(212,168,87,0.15); }
        .gpqv-qty button:disabled { opacity: 0.6; cursor: not-allowed; }
        .gpqv-qty-val {
          color: #fff;
          font-size: 14px;
          font-weight: 700;
          min-width: 36px;
          text-align: center;
          padding: 0 4px;
        }

        /* RESPONSIVE */
        @media (max-width: 991px) {
          .gpqv-grid { grid-template-columns: 1fr; gap: 18px; }
          .gpqv-details-grid { grid-template-columns: 1fr; }
          .gpqv-pkg-grid { grid-template-columns: 1fr 1fr; }
          .gpqv-quick-facts { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 640px) {
          .gpqv-backdrop { padding: 0; align-items: flex-end; }
          .gpqv-modal {
            max-width: 100%;
            max-height: 95vh;
            border-radius: 18px 18px 0 0;
            border-left: none; border-right: none; border-bottom: none;
          }
          .gpqv-body { padding: 18px; }
          .gpqv-title { font-size: 22px; }
          .gpqv-price { font-size: 24px; }
          .gpqv-quick-facts { grid-template-columns: 1fr; }
          .gpqv-pkg-grid { grid-template-columns: 1fr; }
          .gpqv-foot {
            padding: 14px 18px;
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
          }
          .gpqv-view-full { justify-content: center; }
          .gpqv-foot-actions { width: 100%; }
          .gpqv-cart-btn, .gpqv-buy-btn { flex: 1; justify-content: center; }
          .gpqv-image-wrap .image-gallery-image { max-height: 280px !important; }
        }
      `}</style>
    </Fragment>
  );
};

export default ViewModal;
